/*
 * Copyright (c) 2026 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Sunday, May 17th 2026, 10:48:01 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import ListItemText from "@mui/material/ListItemText";
import { Activity as ActivityIcon } from "react-feather";

import { useGlobalScope } from "core/providers";
import { resolveURLName } from "core/routes/utils";
import { APISERVICEPATHS } from "lib/api";
import { getDefaultDownstreamProcessConfiguration } from "lib/factories";
import { DownstreamProcessConfiguration, ProcessDefinition } from "lib/schemas";
import { Link } from "react-router-dom";
import { ProcessDefinitionMany2OnePicker } from "service-builder/components/process-definition/ProcessDefinitionPickers";
import { useDownstreamProcessConfigurationAPIForm } from "service-builder/services";
import { COLORSCHEME } from "theme/styles";
import {
  ConcreteAPIMany2ManyDropDownFormInputProps,
  EditButtonIcon,
  InputHelpDialogWrapper,
  StyledOSDialog,
  EmphasisText,
} from "ui-widgets";
import {
  APIMany2ManyDropDownFormInput,
  OSAPIForm,
  OSAPIFormProps,
  TextFormInput,
  SwitchFormInput,
} from "ui-widgets/forms";
import { useServiceBuilderContext } from "service-builder/providers";

export const DownstreamProcessConfigurationsMany2ManyPicker = <T,>(
  props: ConcreteAPIMany2ManyDropDownFormInputProps<T, DownstreamProcessConfiguration>
) => {
  const getConfigLabel = (config: DownstreamProcessConfiguration) =>
    `${config?.configuration_title} | ${config?.downstream_process_definition_detail?.title}`;

  return (
    <>
      <APIMany2ManyDropDownFormInput<T, DownstreamProcessConfiguration>
        apiServicePath={APISERVICEPATHS.downstreamProcessConfigurations}
        itemIdProperty="id"
        isFetchingAPIItem={false}
        getOptionLabel={getConfigLabel}
        label="Downstream Process Configurations"
        getOptionComponent={(option) => (
          <ListItemText
            primary={option.configuration_title}
            secondary={option.downstream_process_definition_detail?.title}
          />
        )}
        {...props}
        InputProps={{
          startAdornment: <ActivityIcon color={COLORSCHEME.primary} />,
        }}
        renderUpdateItemPopUp={({ item, onUpdateItem, closePopup, popUpOpen }) => {
          return (
            <DownstreamProcessConfigurationModal
              downstreamProcessConfiguration={item}
              onUpdateDownstreamProcessConfiguration={onUpdateItem}
              handleClose={closePopup}
              open={popUpOpen}
            />
          );
        }}
      />
    </>
  );
};

type DownstreamProcessConfigurationModalProps = Parameters<typeof useDownstreamProcessConfigurationAPIForm>[0] & {
  handleClose: () => void;
  open: boolean;
};

export const DownstreamProcessConfigurationModal = ({
  downstreamProcessConfiguration,
  onUpdateDownstreamProcessConfiguration,
  handleClose,
  open,
}: DownstreamProcessConfigurationModalProps) => {
  const {
    globalState: { selectedOrganization },
  } = useGlobalScope();

  const defaultDownstreamProcessConfiguration =
    downstreamProcessConfiguration ?? getDefaultDownstreamProcessConfiguration(selectedOrganization.id);

  const useAPIFormReturn = useDownstreamProcessConfigurationAPIForm({
    downstreamProcessConfiguration: defaultDownstreamProcessConfiguration,
    onUpdateDownstreamProcessConfiguration,
  });

  return (
    <>
      <StyledOSDialog
        popUpIcon={<ActivityIcon color={COLORSCHEME.primary} />}
        popUpTitle="Downstream Process Configuration"
        open={open}
        onClose={handleClose}
        popUpDescription="Provide configuration details for the downstream process."
      >
        <DownstreamProcessConfigurationModalForm useAPIFormReturn={useAPIFormReturn} />
      </StyledOSDialog>
    </>
  );
};

export const DownstreamProcessConfigurationModalForm = ({
  useAPIFormReturn,
  downstreamProcessLabel = "Downstream Process",
  showSubmitButton = true,
}: { downstreamProcessLabel?: string; showSubmitButton?: boolean } & Pick<
  OSAPIFormProps<DownstreamProcessConfiguration>,
  "useAPIFormReturn"
>) => {
  const {
    useFormReturn: { watch },
  } = useAPIFormReturn;
  const watchedAllowMultiInstance = watch("allow_multi_instance");
  const [watchedDownstreamProcess, setWatchedDownstreamProcess] = React.useState<ProcessDefinition>();
  const {
    processDefinitionContext: { currentProcessDefinition },
  } = useServiceBuilderContext();

  return (
    <OSAPIForm useAPIFormReturn={useAPIFormReturn} loaderType="form-skeleton" showSubmitButton={showSubmitButton}>
      <TextFormInput<DownstreamProcessConfiguration> schema_field_key="configuration_title" label="Title" />
      <ProcessDefinitionMany2OnePicker<DownstreamProcessConfiguration>
        schema_field_key="downstream_process_definition"
        label={downstreamProcessLabel}
        inputEndAdornment={
          <Link to={resolveURLName("serviceBuilderRoot", { serviceId: watch("downstream_process_definition") })}>
            <EditButtonIcon />
          </Link>
        }
        onChangeCallBack={setWatchedDownstreamProcess}
      />
      <SwitchFormInput<DownstreamProcessConfiguration>
        schema_field_key="allow_multi_instance"
        label="Allow Multiple Instances"
      />
      <InputHelpDialogWrapper
        helpTitle="Automatically create downstream process."
        helpContent={<>Automatically create a subprocesss instance and pass it the submission</>}
      >
        <SwitchFormInput<DownstreamProcessConfiguration>
          schema_field_key="auto_create_on_node_activation"
          label="Autocreate Subprocess"
        />
      </InputHelpDialogWrapper>
      <InputHelpDialogWrapper
        helpTitle="Hard Dependency"
        helpContent={
          <>
            Setting this option will require an upstream process instance to be selected by the initiator in order for
            the downstream process instance to start.
          </>
        }
      >
        <SwitchFormInput<DownstreamProcessConfiguration>
          schema_field_key="downstream_initiation_requires_upstream_instance"
          label="Hard dependency"
        />
      </InputHelpDialogWrapper>

      {watchedAllowMultiInstance ? (
        <TextFormInput<DownstreamProcessConfiguration> schema_field_key="maximum_instances" label="Maximum Instances" />
      ) : null}
      <InputHelpDialogWrapper
        helpTitle="Show Downstream Tasks"
        helpContent={
          <>
            From within the <EmphasisText>{currentProcessDefinition.title}</EmphasisText> process, show the active tasks
            that are in{" "}
            {watchedDownstreamProcess?.title ? (
              <EmphasisText>{watchedDownstreamProcess.title}</EmphasisText>
            ) : (
              <>the subprocess</>
            )}{" "}
            that a user can execute. This setting is useful for call activities where you may want authorized users to
            conveniently execute tasks in a subprocess context from within the context of the main process.
          </>
        }
      >
        <SwitchFormInput<DownstreamProcessConfiguration>
          schema_field_key="show_downstream_tasks"
          label="Show Downstream Tasks"
        />
      </InputHelpDialogWrapper>
      <InputHelpDialogWrapper
        helpTitle="Show Downstream History"
        helpContent={
          <>
            Include the history of{" "}
            {watchedDownstreamProcess?.title ? (
              <EmphasisText>{watchedDownstreamProcess.title}</EmphasisText>
            ) : (
              <>the subprocess</>
            )}{" "}
            as part of <EmphasisText>{currentProcessDefinition.title}</EmphasisText> history that is displayed.
          </>
        }
      >
        <SwitchFormInput<DownstreamProcessConfiguration>
          schema_field_key="show_downstream_history"
          label="Show Downstream History"
        />
      </InputHelpDialogWrapper>
    </OSAPIForm>
  );
};
